import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import SecaoComercial from "./SecaoComercial";
import SecaoVariacoesEstoque from "./SecaoVariacoesEstoque";
import SecaoBomETecnica from "./SecaoBomETecnica";
import SecaoModelagemCad from "./SecaoModelagemCad";
import {
  Package,
  Search,
  ArrowLeft,
  AlertCircle,
  CheckCircle2,
  Tag,
  Layers,
  Info,
  Ban,
  ChevronRight,
  Folder,
  RefreshCw,
  Plus,
  Camera,
  Disc,
  Boxes,
  Settings2,
  Thermometer,
  Clock,
  Scale,
  Gem
} from "lucide-react";
import { useCatalogContext } from "../../contexts/CatalogContext";
import { getBaseSku, cleanSku, resolveModeloJoia } from "../../utils/skuUtils";
import { pb } from "../../services/pocketbase";
import { produtoDadosService, getArquivosArray, checkHasMolde, extractConfigBorracha } from "../../services/produtoDadosService";
import CriarNovoProdutoModal from "../modals/CriarNovoProdutoModal";
import ConfigBorrachaModal from "../modals/ConfigBorrachaModal";
import TaxonomiaManagerModal from "../modals/TaxonomiaManagerModal";
import { detectCategoryFromProduct } from "../../utils/categoryUtils";

const FALLBACK_TIPOS_JOIA = [
  "Anel",
  "Anel de Formatura",
  "Pingente",
  "Berloque",
  "Brinco",
  "Pulseira",
  "Corrente",
  "Medalha",
  "Aliança",
  "Escapulário",
  "Joia Exclusiva"
];

/**
 * TelaProduto.js — Ficha Única Canônica do SKU Pai
 */
export default function TelaProduto({ skuParam, onBack, onNavigateToSku, onNavigateToCad }) {
  const { catalog, loading: loadingCatalog, findItem, getSubestoqueCera } = useCatalogContext();
  const [searchInput, setSearchInput] = useState("");

  // Resolve o SKU de entrada (getBaseSku -> resolveCatalogSku -> Pai)
  const resolved = useMemo(() => {
    if (!skuParam) return { rawSku: "", skuPai: "", item: null, isVariation: false };

    const rawSku = cleanSku(skuParam);
    const calculatedPai = getBaseSku(rawSku);

    // 1. Busca pelo pai derivado via findItem (que roda resolveCatalogSku)
    let matchedItem = findItem(calculatedPai);

    // 2. Se não encontrou pelo pai derivado, tenta findItem no SKU original
    if (!matchedItem) {
      matchedItem = findItem(rawSku);
    }

    // Identifica o SKU pai canônico real do item resolvido (ou o derivado)
    const finalSkuPai = matchedItem?.sku ? getBaseSku(matchedItem.sku) : calculatedPai;
    const isVariation = rawSku !== finalSkuPai;

    return {
      rawSku,
      skuPai: finalSkuPai,
      item: matchedItem,
      isVariation
    };
  }, [skuParam, catalog, findItem]);

  const { rawSku, skuPai, item, isVariation } = resolved;

  // Carregamento e gestão de Taxonomia (Tipo de Joia + Categoria) e dados do Produto (produto_dados)
  const [produtoDados, setProdutoDados] = useState(null);
  const [cadRecord, setCadRecord] = useState(null);
  const [categoriesList, setCategoriesList] = useState([]);
  const [tiposList, setTiposList] = useState([]);
  const [savingCategory, setSavingCategory] = useState(false);
  const [savingTipoJoia, setSavingTipoJoia] = useState(false);
  const [savingMolde, setSavingMolde] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [isCreateProductModalOpen, setIsCreateProductModalOpen] = useState(false);
  const [isConfigBorrachaModalOpen, setIsConfigBorrachaModalOpen] = useState(false);
  const [isTaxonomiaModalOpen, setIsTaxonomiaModalOpen] = useState(false);

  // Listener para atualização em tempo real de parâmetros da borracha e preço/custo
  useEffect(() => {
    const handleConfigUpdated = (e) => {
      const { skuPai: updatedPai, configBorracha: newConfig } = e.detail || {};
      if (updatedPai && skuPai && getBaseSku(cleanSku(updatedPai)) === skuPai) {
        setProdutoDados(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            ficha_tecnica: {
              ...(prev.ficha_tecnica || {}),
              config_borracha: newConfig
            }
          };
        });
      }
    };

    const handlePrecoUpdated = (e) => {
      const { skuPai: updatedPai, record } = e.detail || {};
      if (updatedPai && skuPai && getBaseSku(cleanSku(updatedPai)) === skuPai && record) {
        setProdutoDados(record);
      }
    };

    window.addEventListener("produto-dados-config-borracha-updated", handleConfigUpdated);
    window.addEventListener("produto-dados-preco-updated", handlePrecoUpdated);
    return () => {
      window.removeEventListener("produto-dados-config-borracha-updated", handleConfigUpdated);
      window.removeEventListener("produto-dados-preco-updated", handlePrecoUpdated);
    };
  }, [skuPai]);

  const fetchProductDataAndTaxonomy = useCallback(async () => {
    if (!skuPai) return;
    try {
      const [dadosRec, catRecs, tipoRecs, cadRecs] = await Promise.all([
        produtoDadosService.getProdutoDados(skuPai, { expand: "categoria,tipo_joia" }),
        pb.collection("produto_categorias").getFullList({ sort: "nome" }).catch(() => []),
        pb.collection("produto_tipos").getFullList({ sort: "nome" }).catch(() => []),
        pb.collection("modelagens").getFullList({ filter: `sku = "${skuPai}"`, sort: "-updated" }).catch(() => [])
      ]);
      setProdutoDados(dadosRec);
      setCategoriesList((catRecs || []).filter(c => c.ativo !== false));
      setTiposList((tipoRecs || []).filter(t => t.ativo !== false));
      setCadRecord(cadRecs && cadRecs.length > 0 ? cadRecs[0] : null);
    } catch (err) {
      console.warn("Aviso ao carregar dados de taxonomia em TelaProduto:", err);
    }
  }, [skuPai]);

  useEffect(() => {
    fetchProductDataAndTaxonomy();
  }, [fetchProductDataAndTaxonomy]);

  // Resolução da Categoria
  const detectedCatObj = useMemo(() => {
    return detectCategoryFromProduct(item, skuPai, categoriesList, produtoDados?.categoria);
  }, [item, skuPai, categoriesList, produtoDados]);

  const activeCategoryName =
    produtoDados?.expand?.categoria?.nome ||
    produtoDados?.ficha_tecnica?.categoria_nome ||
    detectedCatObj?.nome ||
    (item?.category && item.category !== "Não Definido" ? item.category : "Não Definido");

  const selectedCatValue = produtoDados?.categoria || detectedCatObj?.id || "";

  // Resolução do Tipo de Joia
  const resolvedTipoInfo = useMemo(() => {
    // 1. Se tem relação tipo_joia salva em produto_dados
    if (produtoDados?.tipo_joia) {
      const matchById = tiposList.find(t => t.id === produtoDados.tipo_joia);
      if (matchById) {
        return { value: matchById.id, name: matchById.nome };
      }
    }

    // 2. Se tem nome explícito salvo em ficha_tecnica.tipo_joia_nome
    const savedTipoNome = produtoDados?.ficha_tecnica?.tipo_joia_nome;
    if (savedTipoNome) {
      const matchBySavedName = tiposList.find(t => t.nome.toLowerCase() === savedTipoNome.toLowerCase());
      if (matchBySavedName) {
        return { value: matchBySavedName.id, name: matchBySavedName.nome };
      }
      return { value: `nome:${savedTipoNome}`, name: savedTipoNome };
    }

    // 3. Auto-detecção inteligente por SKU / Modelo / Nome
    const autoModelo = resolveModeloJoia(skuPai, item, produtoDados);
    if (autoModelo && autoModelo !== "Outros") {
      // Caso especial: prefixo DIR/MED/BIO/etc. para anéis de formatura
      const prefix = skuPai ? skuPai.split("-")[0]?.toUpperCase() : "";
      const isFormaturaRing = autoModelo === "Anel" && prefix && !["RIN", "ALI", "ANL", "SOL"].includes(prefix);
      const targetName = isFormaturaRing
        ? (tiposList.find(t => t.nome.toLowerCase().includes("formatura"))?.nome || "Anel")
        : autoModelo;

      const matchAuto = tiposList.find(t =>
        t.nome.toLowerCase() === targetName.toLowerCase() ||
        (targetName === "Colar / Corrente" && t.nome.toLowerCase().includes("corrente"))
      );
      if (matchAuto) {
        return { value: matchAuto.id, name: matchAuto.nome };
      }
      return { value: `nome:${autoModelo}`, name: autoModelo };
    }

    return { value: "", name: "Não Definido" };
  }, [produtoDados, tiposList, skuPai, item]);

  const activeTipoJoiaName = resolvedTipoInfo.name;
  const selectedTipoValue = resolvedTipoInfo.value;

  // Alterar Categoria
  const handleSelectCategory = async (selectedCatId) => {
    if (!skuPai) return;
    setSavingCategory(true);
    try {
      const chosenCat = categoriesList.find(c => c.id === selectedCatId);
      const updated = await produtoDadosService.upsertProdutoDados(skuPai, {
        categoria: selectedCatId || null,
        categoria_nome: chosenCat?.nome || ""
      }, { expand: "categoria,tipo_joia" });
      setProdutoDados(updated);

      if (cadRecord && chosenCat?.nome) {
        pb.collection("modelagens").update(cadRecord.id, {
          categoria: chosenCat.nome
        }).catch(() => { });
      }
    } catch (err) {
      console.error("Erro ao vincular categoria no produto_dados:", err);
      alert("Não foi possível salvar a categoria: " + err.message);
    } finally {
      setSavingCategory(false);
    }
  };

  // Alterar Tipo de Joia
  const handleSelectTipoJoia = async (selectedVal) => {
    if (!skuPai) return;
    setSavingTipoJoia(true);
    try {
      let tipoId = null;
      let tipoNome = "";

      if (selectedVal.startsWith("nome:")) {
        tipoNome = selectedVal.replace("nome:", "");
        const matched = tiposList.find(t => t.nome.toLowerCase() === tipoNome.toLowerCase());
        if (matched) tipoId = matched.id;
      } else if (selectedVal) {
        const matched = tiposList.find(t => t.id === selectedVal);
        if (matched) {
          tipoId = matched.id;
          tipoNome = matched.nome;
        }
      }

      const currentFicha = produtoDados?.ficha_tecnica && typeof produtoDados.ficha_tecnica === "object"
        ? produtoDados.ficha_tecnica
        : {};

      const updated = await produtoDadosService.upsertProdutoDados(skuPai, {
        tipo_joia: tipoId,
        tipo_joia_nome: tipoNome,
        ficha_tecnica: {
          ...currentFicha,
          tipo_joia_nome: tipoNome,
          config_borracha: {
            ...(currentFicha.config_borracha || {}),
            tipo_modelo: tipoNome || currentFicha.config_borracha?.tipo_modelo || ""
          }
        }
      }, { expand: "categoria,tipo_joia" });

      setProdutoDados(updated);

      if (cadRecord && tipoNome) {
        pb.collection("modelagens").update(cadRecord.id, {
          tipo_joia: tipoNome
        }).catch(() => { });
      }
    } catch (err) {
      console.error("Erro ao salvar tipo de joia:", err);
      alert("Não foi possível salvar o tipo de joia: " + err.message);
    } finally {
      setSavingTipoJoia(false);
    }
  };

  // Auto-associa categoria no PocketBase se produtoDados existe mas não tem categoria salva
  const autoAssignedCatRef = useRef(null);
  useEffect(() => {
    if (
      detectedCatObj?.id &&
      produtoDados &&
      !produtoDados.categoria &&
      !savingCategory &&
      autoAssignedCatRef.current !== `${skuPai}-${detectedCatObj.id}`
    ) {
      autoAssignedCatRef.current = `${skuPai}-${detectedCatObj.id}`;
      handleSelectCategory(detectedCatObj.id);
    }
  }, [detectedCatObj, produtoDados, skuPai, savingCategory]);

  // Toggle de Molde de Borracha (Fluxo sem impressão 3D -> Direto Separação/Injeção)
  const hasMolde = checkHasMolde(produtoDados, item);
  const configBorracha = extractConfigBorracha(produtoDados, item);

  const handleToggleMolde = async () => {
    if (!skuPai || savingMolde) return;
    const nextVal = !hasMolde;
    setSavingMolde(true);
    try {
      const updated = await produtoDadosService.updateTemMolde(skuPai, nextVal);
      if (updated) {
        setProdutoDados(updated);
      }
    } catch (err) {
      console.error("Erro ao alterar status de molde:", err);
      alert("Não foi possível salvar a alteração de molde: " + err.message);
    } finally {
      setSavingMolde(false);
    }
  };

  // Upload Manual de Foto de Capa direto pelo cabeçalho
  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !skuPai) return;

    setUploadingCover(true);
    try {
      const updated = await produtoDadosService.uploadImages(skuPai, [file]);
      if (updated) {
        setProdutoDados(updated);
      }
      try {
        const catRecs = await pb.collection("catalog").getFullList({ filter: `sku = "${skuPai}"` });
        if (catRecs.length > 0) {
          const imgUrl = pb.files.getUrl(updated, file.name);
          await pb.collection("catalog").update(catRecs[0].id, {
            image_url: imgUrl,
            full_data: {
              ...(catRecs[0].full_data || {}),
              image_default: imgUrl
            }
          });
        }
      } catch (catErr) {
        console.warn("Aviso ao atualizar capa no catálogo:", catErr);
      }
    } catch (err) {
      alert("Erro ao enviar foto: " + err.message);
    } finally {
      setUploadingCover(false);
      e.target.value = "";
    }
  };

  // Resolução da Imagem Principal (Catálogo > produto_dados.arquivos_imagens > cadRecord.arquivos)
  const resolvedCoverImage = useMemo(() => {
    if (item?.image || item?.image_url || item?.image_default) {
      return item.image || item.image_url || item.image_default;
    }

    const localImgs = getArquivosArray(produtoDados?.arquivos_imagens);
    const imgFile = localImgs.find(f => {
      const name = typeof f === "string" ? f : f.name;
      const ext = name?.split('.').pop()?.toLowerCase();
      return ["png", "jpg", "jpeg", "webp", "gif"].includes(ext);
    });
    if (imgFile && produtoDados) {
      const fileName = typeof imgFile === "string" ? imgFile : imgFile.name;
      return pb.files.getUrl(produtoDados, fileName);
    }

    const cadImgs = getArquivosArray(cadRecord?.arquivos);
    const cadImgFile = cadImgs.find(f => {
      const name = typeof f === "string" ? f : f.name;
      const ext = name?.split('.').pop()?.toLowerCase();
      return ["png", "jpg", "jpeg", "webp", "gif"].includes(ext);
    });
    if (cadImgFile && cadRecord) {
      const fileName = typeof cadImgFile === "string" ? cadImgFile : cadImgFile.name;
      return pb.files.getUrl(cadRecord, fileName);
    }

    return null;
  }, [item, produtoDados, cadRecord]);

  // Atualiza o input de busca ao mudar de SKU
  useEffect(() => {
    setSearchInput(skuParam || "");
  }, [skuParam]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (!searchInput.trim()) return;
    const cleanSearch = cleanSku(searchInput);
    if (onNavigateToSku) {
      onNavigateToSku(cleanSearch);
    } else {
      window.location.hash = `#produto/${cleanSearch}`;
    }
  };

  const isInactive = item?.isInactive || item?.status === "inactive" || item?.full_data?.status === "inactive";

  return (
    <div className="flex flex-col w-full h-full bg-slate-50 overflow-y-auto pb-20">
      {/* HEADER DE NAVEGAÇÃO & BUSCA */}
      <div className="bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex flex-col md:flex-row md:items-center justify-between gap-3 shrink-0 shadow-sm">
        <div className="flex items-center gap-3">
          {onBack && (
            <>
              <button
                onClick={onBack}
                className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors flex items-center gap-1 text-sm font-medium cursor-pointer"
                title="Voltar"
              >
                <ArrowLeft size={18} />
                <span>Voltar</span>
              </button>
              <div className="h-5 w-px bg-slate-200" />
            </>
          )}
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
            <Package size={16} className="text-blue-600" />
            <span>Ficha do Produto (SKU Pai)</span>
          </div>
        </div>

        {/* BUSCA RÁPIDA DE SKU E BOTÃO DE CRIAR */}
        <div className="flex items-center gap-2 w-full md:max-w-lg">
          <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 flex-1">
            <div className="relative w-full">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Buscar SKU (ex: DIR-AUR)..."
                className="w-full pl-9 pr-4 py-1.5 bg-slate-100 border border-slate-200 rounded-lg text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all uppercase"
              />
            </div>
            <button
              type="submit"
              className="px-3 py-1.5 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-bold transition-colors whitespace-nowrap cursor-pointer"
            >
              Abrir
            </button>
          </form>

          <button
            type="button"
            onClick={() => setIsCreateProductModalOpen(true)}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold transition-colors whitespace-nowrap shadow flex items-center gap-1 shrink-0 cursor-pointer"
          >
            <Plus size={14} />
            <span>+ Criar Produto</span>
          </button>
        </div>
      </div>

      {/* CONTEÚDO DA TELA */}
      <div className="w-full p-3 sm:p-6">
        {loadingCatalog ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400 gap-3">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm font-medium">Carregando catálogo do produto...</p>
          </div>
        ) : !skuParam ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400 gap-3">
            <Package size={48} className="stroke-1 text-slate-300" />
            <p className="text-base font-semibold text-slate-600">Nenhum SKU informado</p>
            <p className="text-sm text-slate-400">Digite um SKU na barra de busca acima para visualizar a ficha do produto.</p>
          </div>
        ) : (
          <div className="max-w-6xl mx-auto space-y-6">

            {/* CABEÇALHO DO PRODUTO */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6 shadow-sm flex flex-col gap-5">
              <div className="flex flex-col lg:flex-row gap-5 lg:gap-6 items-start lg:items-center justify-between">

                {/* ESQUERDA: FOTO E DETALHES PRINCIPAIS */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 w-full lg:w-auto">
                  {/* FOTO DO PRODUTO COM UPLOAD DIRETO */}
                  <div className="relative w-28 h-28 bg-slate-100 border border-slate-200 rounded-xl overflow-hidden flex items-center justify-center shrink-0 shadow-inner group">
                    {resolvedCoverImage ? (
                      <img
                        src={resolvedCoverImage}
                        alt={item?.name || skuPai}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                          const fallback = e.target.nextSibling;
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      className="w-full h-full flex flex-col items-center justify-center text-slate-300 bg-slate-50"
                      style={{ display: resolvedCoverImage ? 'none' : 'flex' }}
                    >
                      <Package size={32} />
                      <span className="text-[10px] mt-1 text-slate-400 font-medium">Sem foto</span>
                    </div>

                    {/* OVERLAY INTERATIVO: SUBIR OU TROCAR FOTO */}
                    <label
                      className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white cursor-pointer p-1 text-center backdrop-blur-[2px] select-none"
                      title="Clique para enviar ou trocar a foto de capa deste produto"
                    >
                      {uploadingCover ? (
                        <RefreshCw size={20} className="animate-spin text-white mb-1" />
                      ) : (
                        <Camera size={20} className="text-white mb-1" />
                      )}
                      <span className="text-[9px] font-bold leading-tight uppercase">
                        {uploadingCover ? "Salvando..." : (resolvedCoverImage ? "Trocar Foto" : "Subir Foto")}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleCoverUpload}
                        disabled={uploadingCover}
                        className="hidden"
                      />
                    </label>
                  </div>

                  {/* INFO DO PRODUTO */}
                  <div className="space-y-2.5">
                    <div className="flex flex-wrap items-center gap-2">
                      {/* BADGE DE SKU PAI */}
                      <span className="px-2.5 py-1 bg-slate-900 text-white rounded-md text-xs font-mono font-bold tracking-wider flex items-center gap-1.5">
                        <Tag size={13} className="text-amber-400" />
                        SKU PAI: {skuPai}
                      </span>

                      {/* BADGE SE FOI BUSCADO POR VARIAÇÃO (FILHO) */}
                      {isVariation && (
                        <span className="px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-md text-xs font-medium flex items-center gap-1">
                          <Layers size={13} />
                          Entrada via variação: <strong className="font-mono">{rawSku}</strong>
                        </span>
                      )}

                      {/* BADGE DE STATUS DE INATIVIDADE */}
                      {isInactive ? (
                        <span className="px-2.5 py-1 bg-red-100 text-red-700 border border-red-200 rounded-md text-xs font-bold flex items-center gap-1">
                          <Ban size={13} />
                          Inativo na loja
                        </span>
                      ) : (
                        <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-md text-xs font-medium flex items-center gap-1">
                          <CheckCircle2 size={13} />
                          Ativo no catálogo
                        </span>
                      )}

                      {/* TOGGLE INTERATIVO DE MOLDE DE BORRACHA */}
                      <button
                        type="button"
                        onClick={handleToggleMolde}
                        disabled={savingMolde}
                        title={hasMolde
                          ? "Clique para desativar. Este SKU usa molde de borracha (injeção direta sem impressão 3D)."
                          : "Clique para ativar molde de borracha para este SKU (pula impressão 3D e vai direto para Separação/Injeção -> Fundição)."
                        }
                        className={`px-3 py-1 rounded-md text-xs font-bold transition-all flex items-center gap-2 border shadow-sm cursor-pointer select-none ${hasMolde
                            ? "bg-gradient-to-r from-amber-500 to-amber-600 text-white border-amber-600 hover:from-amber-600 hover:to-amber-700 ring-2 ring-amber-300/50"
                            : "bg-slate-100 text-slate-600 border-slate-300 hover:bg-slate-200 hover:text-slate-800"
                          }`}
                      >
                        {savingMolde ? (
                          <RefreshCw size={13} className="animate-spin text-current" />
                        ) : (
                          <Disc size={13} className={hasMolde ? "text-amber-100" : "text-slate-400"} />
                        )}
                        <span>
                          {hasMolde ? "MOLDE DE BORRACHA: LIGADO" : "MOLDE: DESLIGADO (3D)"}
                        </span>
                        <span className={`w-3.5 h-3.5 rounded-full border transition-all flex items-center justify-center ${hasMolde ? "bg-white border-amber-200" : "bg-slate-300 border-slate-400"
                          }`}>
                          {hasMolde && <span className="w-1.5 h-1.5 rounded-full bg-amber-600" />}
                        </span>
                      </button>

                      {/* MINI-INDICADOR DE SUBESTOQUE DE CERAS */}
                      {hasMolde && (
                        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 px-3 py-1 rounded-md text-xs font-semibold text-amber-950 shadow-sm">
                          <Boxes size={14} className="text-amber-700" />
                          <span>Ceras: <strong>{(getSubestoqueCera(skuPai)?.cera_bruta || 0)} brutas</strong> | <strong>{(getSubestoqueCera(skuPai)?.cera_precravada || 0)} cravadas</strong></span>
                          {getSubestoqueCera(skuPai)?.localizacao_gaveta && (
                            <span className="text-[10px] text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded font-bold border border-amber-300">
                              📍 {getSubestoqueCera(skuPai).localizacao_gaveta}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* NOME DO PRODUTO */}
                    <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
                      {item?.name || `Produto ${skuPai}`}
                    </h1>

                    {!item && (
                      <span className="text-amber-600 text-xs font-normal flex items-center gap-1">
                        <AlertCircle size={14} />
                        Este SKU não possui registro direto no catálogo, mas está ancorado no pai <strong className="font-mono">{skuPai}</strong>.
                      </span>
                    )}
                  </div>
                </div>

                {/* DIREITA: SELO DE REGRA E ESTADO DO FLUXO */}
                <div className={`border rounded-lg p-3 text-xs w-full lg:max-w-xs space-y-1.5 transition-all shrink-0 ${hasMolde
                    ? "bg-amber-50/80 border-amber-200 text-amber-900"
                    : "bg-slate-50 border-slate-200 text-slate-600"
                  }`}>
                  <div className="font-bold flex items-center gap-1.5">
                    {hasMolde ? (
                      <>
                        <Disc size={15} className="text-amber-600 shrink-0" />
                        <span className="text-amber-800">Fluxo Molde de Borracha</span>
                      </>
                    ) : (
                      <>
                        <Info size={15} className="text-blue-600 shrink-0" />
                        <span className="text-slate-800">Fluxo Padrão (Impressão 3D)</span>
                      </>
                    )}
                  </div>
                  <p className="text-[11px] leading-relaxed text-slate-500">
                    {hasMolde ? (
                      <span>
                        Este produto <strong className="text-amber-800">não passa por impressão 3D</strong>. Entra na fila sinalizado com a tag <strong className="text-amber-800">BORRACHA</strong> e vai direto para <strong className="text-amber-800">Separação/Injeção &rarr; Fundição</strong>.
                      </span>
                    ) : (
                      <span>
                        Esta ficha reúne comercial, estoque, receitas BOM e modelagem pelo SKU pai <span className="font-mono text-slate-700">{skuPai}</span>.
                      </span>
                    )}
                  </p>
                </div>
              </div>

              {/* BARRA CLARA DE CLASSIFICAÇÃO: ALTERAR TIPO DE JOIA E CATEGORIA */}
              <div className="pt-4 border-t border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1 max-w-3xl">

                  {/* SELETOR 1: TIPO DE JOIA */}
                  <div className="bg-slate-50 border border-slate-200 hover:border-amber-300 rounded-xl px-3.5 py-2 transition-colors flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                      <div className="p-1.5 bg-amber-100 text-amber-700 rounded-lg shrink-0">
                        <Gem size={15} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                          Tipo de Joia
                        </label>
                        <select
                          value={selectedTipoValue}
                          onChange={(e) => handleSelectTipoJoia(e.target.value)}
                          disabled={savingTipoJoia}
                          className="w-full bg-transparent text-slate-900 font-bold text-xs outline-none cursor-pointer truncate mt-0.5"
                        >
                          <option value="">Selecionar Tipo de Joia...</option>
                          {tiposList.map(t => (
                            <option key={t.id} value={t.id}>
                              {t.nome} {t.prefixo_sku ? `(${t.prefixo_sku})` : ""}
                            </option>
                          ))}
                          {FALLBACK_TIPOS_JOIA.filter(
                            nome => !tiposList.some(t => t.nome.toLowerCase() === nome.toLowerCase())
                          ).map(nome => (
                            <option key={`nome:${nome}`} value={`nome:${nome}`}>
                              {nome}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {savingTipoJoia && <RefreshCw size={13} className="animate-spin text-amber-600 shrink-0" />}
                  </div>

                  {/* SELETOR 2: CATEGORIA / CURSO */}
                  <div className="bg-slate-50 border border-slate-200 hover:border-blue-300 rounded-xl px-3.5 py-2 transition-colors flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                      <div className="p-1.5 bg-blue-100 text-blue-700 rounded-lg shrink-0">
                        <Folder size={15} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                          Categoria / Curso
                        </label>
                        <select
                          value={selectedCatValue}
                          onChange={(e) => handleSelectCategory(e.target.value)}
                          disabled={savingCategory}
                          className="w-full bg-transparent text-slate-900 font-bold text-xs outline-none cursor-pointer truncate mt-0.5"
                        >
                          <option value="">Selecionar Categoria...</option>
                          {categoriesList.map(cat => (
                            <option key={cat.id} value={cat.id}>
                              {cat.nome}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {savingCategory && <RefreshCw size={13} className="animate-spin text-blue-600 shrink-0" />}
                  </div>

                </div>

                {/* BOTÃO PARA ABRIR GESTOR DE TAXONOMIA (CRIAR/EDITAR TIPOS E CATEGORIAS) */}
                <button
                  type="button"
                  onClick={() => setIsTaxonomiaModalOpen(true)}
                  className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-colors flex items-center gap-1.5 self-start md:self-auto shrink-0 cursor-pointer"
                  title="Criar ou gerenciar Tipos de Joia e Categorias"
                >
                  <Settings2 size={14} className="text-slate-500" />
                  <span>Gerenciar Tipos & Categorias</span>
                </button>
              </div>

            </div>

            {/* SEÇÃO TÉCNICA DE MOLDE DE BORRACHA E INJEÇÃO (PRESERVADA SE MOLDE ATIVO) */}
            {hasMolde && (
              <div className="bg-white border border-amber-300/90 rounded-xl p-4 sm:p-5 shadow-xs space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-amber-100 pb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-amber-500 text-white rounded-xl shadow-xs">
                      <Settings2 size={20} />
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-sm font-black uppercase tracking-wider text-slate-900">
                          Parâmetros da Injetora & Molde de Borracha
                        </h2>
                        {configBorracha.codigo_maquina ? (
                          <span className="font-mono text-xs font-black bg-amber-100 text-amber-900 border border-amber-300 px-2 py-0.5 rounded shadow-2xs">
                            ⚙️ CÓDIGO: {configBorracha.codigo_maquina}
                          </span>
                        ) : (
                          <span className="text-[10px] bg-slate-100 text-slate-500 font-semibold px-2 py-0.5 rounded border border-slate-200">
                            Sem código da máquina
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Configurações para injeção de cera, painel de 6 dígitos e receitas de pré-cravação
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setIsConfigBorrachaModalOpen(true)}
                    className="px-3.5 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold rounded-lg transition shadow-xs flex items-center gap-1.5 self-start sm:self-auto cursor-pointer"
                  >
                    <Settings2 size={13} />
                    <span>Configurar Borracha</span>
                  </button>
                </div>

                {/* GRID DE PARÂMETROS DA INJEÇÃO */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
                  <div className="bg-amber-50/70 border border-amber-200 rounded-lg p-2.5">
                    <span className="text-[10px] font-bold text-amber-800 uppercase block">Código (6 dígitos)</span>
                    <span className="font-mono text-sm font-black text-amber-950 block mt-0.5">
                      {configBorracha.codigo_maquina || "—"}
                    </span>
                  </div>

                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5">
                    <span className="text-[10px] font-bold text-slate-500 uppercase flex items-center gap-1">
                      <Thermometer size={11} className="text-amber-600" /> Tanque / Bico
                    </span>
                    <span className="text-xs font-bold text-slate-800 block mt-0.5">
                      {configBorracha.temperatura_tanque || "--"} / {configBorracha.temperatura_bico || "--"}
                    </span>
                  </div>

                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5">
                    <span className="text-[10px] font-bold text-slate-500 uppercase flex items-center gap-1">
                      <Clock size={11} className="text-blue-500" /> Vácuo
                    </span>
                    <span className="text-xs font-bold text-slate-800 block mt-0.5">
                      {configBorracha.tempo_vacuo ? `${configBorracha.tempo_vacuo}` : "--"}
                    </span>
                  </div>

                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5">
                    <span className="text-[10px] font-bold text-slate-500 uppercase flex items-center gap-1">
                      <Scale size={11} className="text-emerald-600" /> Peso Cera
                    </span>
                    <span className="text-xs font-bold text-slate-800 block mt-0.5">
                      {configBorracha.peso_cera_g ? `${configBorracha.peso_cera_g}g` : "--"}
                    </span>
                  </div>

                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5">
                    <span className="text-[10px] font-bold text-slate-500 uppercase block">Qtd Moldes</span>
                    <span className="text-xs font-bold text-slate-800 block mt-0.5">
                      {configBorracha.qtd_moldes || "1 un"}
                    </span>
                  </div>

                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5">
                    <span className="text-[10px] font-bold text-slate-500 uppercase block">Modelo</span>
                    <span className="text-xs font-bold text-slate-800 block mt-0.5 truncate">
                      {configBorracha.tipo_modelo || activeTipoJoiaName}
                    </span>
                  </div>
                </div>

                {/* Pedras e Observações */}
                <div className="flex flex-col sm:flex-row gap-3 pt-1">
                  <div className="flex-1 bg-purple-50/50 border border-purple-200 rounded-lg p-2.5">
                    <span className="text-[10px] font-bold text-purple-800 uppercase flex items-center gap-1">
                      <Gem size={12} className="text-purple-600" /> Pedras Padrão p/ Pré-Cravação
                    </span>
                    <p className="text-xs font-medium text-slate-800 mt-1">
                      {configBorracha.pedras_resumo || (
                        <span className="text-slate-400 italic">Nenhuma pedra padrão definida</span>
                      )}
                    </p>
                  </div>

                  {configBorracha.observacoes && (
                    <div className="flex-1 bg-amber-50/40 border border-amber-200/80 rounded-lg p-2.5">
                      <span className="text-[10px] font-bold text-amber-800 uppercase block">
                        Observações da Injeção
                      </span>
                      <p className="text-xs text-slate-700 mt-1 leading-relaxed">
                        {configBorracha.observacoes}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* SEÇÃO COMERCIAL UNIFICADA (PREÇO, CUSTO, RECEITA BOM EM MODAL & VENDAS) */}
            <SecaoComercial
              skuPai={skuPai}
              item={item}
              produtoDados={produtoDados}
              activeTipoJoiaName={activeTipoJoiaName}
              onProdutoDadosUpdated={setProdutoDados}
            />

            {/* SEÇÃO VARIAÇÕES + ESTOQUE */}
            <SecaoVariacoesEstoque skuPai={skuPai} />

            {/* SEÇÃO DE MODELAGEM CAD, ANEXOS & LINKS */}
            <SecaoModelagemCad
              skuPai={skuPai}
              item={item}
              activeCategoryName={activeCategoryName}
              activeTipoJoiaName={activeTipoJoiaName}
              onProdutoDadosUpdated={setProdutoDados}
              onNavigateToCad={onNavigateToCad}
            />

            {/* SEÇÃO FICHA TÉCNICA E ANÚNCIO IA */}
            <SecaoBomETecnica
              skuPai={skuPai}
              item={item}
              produtoDados={produtoDados}
              onProdutoDadosUpdated={setProdutoDados}
            />

          </div>
        )}
      </div>

      {/* MODAL DE CRIAÇÃO DE NOVO PRODUTO */}
      {isCreateProductModalOpen && (
        <CriarNovoProdutoModal
          isOpen={isCreateProductModalOpen}
          onClose={() => setIsCreateProductModalOpen(false)}
          onSuccess={(newSku) => {
            if (onNavigateToSku) {
              onNavigateToSku(newSku);
            }
          }}
        />
      )}

      {/* MODAL DE CONFIGURAÇÃO DA BORRACHA */}
      {isConfigBorrachaModalOpen && (
        <ConfigBorrachaModal
          isOpen={isConfigBorrachaModalOpen}
          onClose={() => setIsConfigBorrachaModalOpen(false)}
          skuPai={skuPai}
          initialConfig={configBorracha}
          onSuccess={(savedConfig) => {
            setProdutoDados(prev => {
              if (!prev) return prev;
              return {
                ...prev,
                ficha_tecnica: {
                  ...(prev.ficha_tecnica || {}),
                  config_borracha: savedConfig
                }
              };
            });
          }}
        />
      )}

      {/* MODAL DE GERENCIAMENTO DE TAXONOMIA (TIPOS DE JOIA & CATEGORIAS) */}
      {isTaxonomiaModalOpen && (
        <TaxonomiaManagerModal
          isOpen={isTaxonomiaModalOpen}
          onClose={() => setIsTaxonomiaModalOpen(false)}
          onSuccess={fetchProductDataAndTaxonomy}
        />
      )}
    </div>
  );
}
